/**
 * Command Autocomplete Component
 * Provides slash command discovery and autocomplete UI
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { slashCommandSystem, CommandSegment } from '@/services/ai/commands/SlashCommandSystem';
import { CommandCategory, slashCommands } from '@/services/ai/commands/slashCommands';
import { ChevronRight, Command, Palette, Layout, Settings, FileText, Layers, MoreHorizontal } from 'lucide-react';

interface CommandAutocompleteProps {
  input: string;
  onSelectCommand: (command: string) => void;
  isVisible: boolean;
  position?: { top: number; left: number };
}

const categoryIcons: Record<CommandCategory, React.ReactNode> = {
  style: <Palette className="w-4 h-4" />,
  layout: <Layout className="w-4 h-4" />,
  component: <Layers className="w-4 h-4" />,
  action: <Settings className="w-4 h-4" />,
  data: <Command className="w-4 h-4" />,
  template: <FileText className="w-4 h-4" />
};

const categoryColors: Record<CommandCategory, string> = {
  style: 'text-blue-500',
  layout: 'text-green-500',
  component: 'text-purple-500',
  action: 'text-orange-500',
  data: 'text-cyan-500',
  template: 'text-pink-500'
};

export const CommandAutocomplete: React.FC<CommandAutocompleteProps> = ({
  input,
  onSelectCommand,
  isVisible,
  position
}) => {
  const [segments, setSegments] = useState<CommandSegment[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [currentPath, setCurrentPath] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isVisible) {
      setSelectedIndex(0);
      setCurrentPath('');
      return;
    }

    // Parse the input to get the command part
    const trimmed = input.trim();
    if (!trimmed.startsWith('/')) {
      setSegments([]);
      return;
    }

    // Get progressive segments for current input
    const progressiveSegments = slashCommandSystem.getProgressiveSegments(trimmed);
    setSegments(progressiveSegments);

    // Reset selected index when segments change
    setSelectedIndex(0);
    setCurrentPath('');
  }, [input, isVisible]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!isVisible || segments.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => (prev + 1) % segments.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => (prev - 1 + segments.length) % segments.length);
        break;
      case 'Tab':
      case 'Enter':
        e.preventDefault();
        if (segments[selectedIndex]) {
          const segment = segments[selectedIndex];
          // Use the full command value and add a space for parameters
          const newCommand = '/' + segment.value + ' ';
          onSelectCommand(newCommand);
        }
        break;
      case 'Escape':
        e.preventDefault();
        onSelectCommand(input); // Keep current input but close autocomplete
        break;
    }
  }, [isVisible, segments, selectedIndex, onSelectCommand, input, currentPath]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  if (!isVisible || segments.length === 0) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      className="absolute z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-96 overflow-y-auto"
      style={{
        bottom: position ? `calc(100% + ${position.top}px)` : 'calc(100% + 8px)',
        left: position?.left || 0,
        minWidth: '250px',
        maxWidth: '400px'
      }}
    >
      <div className="p-2">
        {/* Show segments */}
        {segments.map((segment, idx) => (
          <SegmentOption
            key={segment.value}
            segment={segment}
            isSelected={selectedIndex === idx}
            onClick={() => {
              // Use the full command value and add a space for parameters
              const newCommand = '/' + segment.value + ' ';
              onSelectCommand(newCommand);
            }}
          />
        ))}
      </div>

      <div className="border-t border-gray-200 dark:border-gray-700 px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
        <span className="font-medium">Tab/Enter</span> to select • <span className="font-medium">↑↓</span> to navigate • <span className="font-medium">Esc</span> to close
      </div>
    </div>
  );
};

interface SegmentOptionProps {
  segment: CommandSegment;
  isSelected: boolean;
  onClick: () => void;
}

const SegmentOption: React.FC<SegmentOptionProps> = ({ segment, isSelected, onClick }) => {
  const handleClick = () => {
    console.log('✅ CommandAutocomplete: Selected segment:', segment.value);
    onClick();
  };

  // Get the actual command object to access aliases
  const commandWithAliases = React.useMemo(() => {
    return slashCommands.find(cmd => cmd.name === segment.value);
  }, [segment.value]);

  // Determine icon to show
  const getIcon = () => {
    if (segment.category && categoryIcons[segment.category]) {
      return categoryIcons[segment.category];
    }
    if (segment.hasChildren) {
      return <MoreHorizontal className="w-4 h-4" />;
    }
    return null;
  };

  return (
    <div
      className={`px-3 py-2 rounded cursor-pointer transition-colors flex items-center gap-3 ${
        isSelected
          ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
          : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
      }`}
      onClick={handleClick}
    >
      {/* Icon or indicator */}
      {getIcon() && (
        <span className={`flex-shrink-0 ${segment.category ? categoryColors[segment.category] : 'text-gray-400'}`}>
          {getIcon()}
        </span>
      )}

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          {/* Show the full command */}
          <span className="font-medium text-sm">/{segment.value}</span>

          {/* Show aliases if available */}
          {commandWithAliases?.aliases && commandWithAliases.aliases.length > 0 && (
            <span className="text-xs text-gray-400 dark:text-gray-500">
              ({commandWithAliases.aliases.map((alias: string) => `/${alias}`).join(', ')})
            </span>
          )}

          {/* Show arrow if selected */}
          {isSelected && (
            <ChevronRight className="w-3 h-3 ml-auto flex-shrink-0 text-gray-400" />
          )}
        </div>

        {/* Show description */}
        {segment.description && (
          <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
            {segment.description}
          </div>
        )}
      </div>
    </div>
  );
};
